import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  TabContent,
  TabPane,
  Input
} from 'reactstrap';
import * as Yup from 'yup';
import Loader from '../components/Loader';
import { departmentType, source, visitReason } from '../constants';
import { displayError, displaySuccess, logAmplitudeEvent } from '../utilities';
import {
  GET_ALL_HOSPITALS,
  getDxLtEdd,
  updatePatientDetails
} from '../queries';
import { fetchCustomerDetails } from '../utilities/fetchCustomerDetails';
import moment from 'moment';
import DynamicSelect from './DynamicSelect';
import { useQuery } from 'react-apollo';

const TABS = {
  CUSTOMER_DETAILS: 1
};

// eslint-disable-next-line react/prop-types
const ErrorMessage = ({ message }) => {
  return <div style={{ color: 'red' }}>{message}</div>;
};

const CustomerDetailsModal = ({ isOpen, toggle, userMPID }) => {
  const [selectedTab, setSelectedTab] = useState(TABS.CUSTOMER_DETAILS);
  const [loaderIcon, setLoaderIcon] = useState(true);
  const [customerDetails, setCustomerDetails] = useState([]);
  const [detailsLoaderIconVisible, setDetailsLoaderIconVisible] = useState(
    false
  );
  const [dxLtEdd, setDxLtEdd] = useState({});

  const { data: { hospitals = [] } = {} } = useQuery(GET_ALL_HOSPITALS, {
    variables: { showAll: false }
  });

  const formattedHospitals = hospitals.map(hospital => ({
    label: hospital.name,
    value: hospital.id
  }));

  const userDetails = JSON.parse(localStorage.getItem('user'));
  const userAllocatedHospital =
    userDetails.hospitals && userDetails.hospitals.length === 0
      ? formattedHospitals
      : hospitals
          .filter(hospital => userDetails.hospitals.includes(hospital.id))
          .map(hospital => ({
            value: hospital.id,
            label: hospital.name
          }));

  useEffect(() => {
    if (!isOpen) return;

    async function fetchData() {
      try {
        setLoaderIcon(true);
        setDetailsLoaderIconVisible(false);

        // fetch customer details
        const customerData = await fetchCustomerDetails(userMPID);
        if (customerData) {
          setCustomerDetails(customerData);
        }

        // fetch EDD
        const eddData = await getDxLtEdd(userMPID);
        setDxLtEdd(eddData.data.getDxAndLtEdd || {});

        setSelectedTab(TABS.CUSTOMER_DETAILS);

        // log event
        logAmplitudeEvent(
          'Customer_Details_Icon_Pressed',
          { userMPID, selectedTab: 'PATIENT DETAILS' },
          true
        );
      } catch (error) {
        console.error('Error fetching customer or EDD', error);
      } finally {
        setLoaderIcon(false);
      }
    }

    fetchData();
  }, [isOpen, toggle, userMPID]);

  // function to update customer's information.
  const handlePatientUpdate = values => {
    setDetailsLoaderIconVisible(true);
    const getUserName = values.name.trim().split(' ');
    const req_body = {
      input: {
        mpi: userMPID,
        registeredSiteCode: values.registeredCenter,
        gender: values.gender,
        dateOfBirth: values.dateOfBirth,
        firstName: getUserName[0] || '',
        lastName: getUserName.slice(1).join(' ') || '',
        emergencyContactName: values.emergencyContactName || '',
        emergencyContactMobile: values.emergencyContactMobile || '',
        email: values.email,
        edd:
          !['DX_APP'].includes(dxLtEdd.source) && values.edd
            ? moment(values.edd).format('YYYY-MM-DD')
            : '',
        departmentType: values.departmentType,
        visitReason: values.visitReason,
        source: values.source,
        remarks: values.remarks,
        vcReferral: values.vcReferral,
        pincode: values.pincode,
        hospitalId: values.hospitalId || null
      }
    };

    updatePatientDetails(req_body)
      .then(response => {
        if (
          response.data &&
          response.data.updatePatientDetails &&
          response.data.updatePatientDetails.message &&
          response.data.updatePatientDetails.message === 'Updated successfully'
        ) {
          setDetailsLoaderIconVisible(false);
          displaySuccess('Details updated successfully');
          logAmplitudeEvent(
            'Patient_Details_Updated_Successfully',
            {
              selectedTab: 'PATIENT DETAILS',
              userMPID: userMPID,
              userName: values.name.trim(),
              dateOfBirth: values.dateOfBirth,
              gender: values.gender,
              emergencyContactName: values.emergencyContactName,
              emergencyContactMobile: values.emergencyContactMobile,
              email: values.email,
              edd: moment(values.edd).format('YYYY-MM-DD') || ''
            },
            true
          );
        } else {
          setDetailsLoaderIconVisible(false);
          displayError('Update failed. Please try again');
          logAmplitudeEvent(
            'Patient_Details_Updation_Failed',
            {
              selectedTab: 'PATIENT DETAILS',
              userMPID: userMPID,
              userName: values.name.trim(),
              dateOfBirth: values.dateOfBirth,
              gender: values.gender,
              emergencyContactName: values.emergencyContactName,
              emergencyContactMobile: values.emergencyContactMobile,
              email: values.email,
              response: response.data,
              edd: moment(values.edd).format('YYYY-MM-DD') || ''
            },
            true
          );
        }
      })
      .catch(error => {
        setDetailsLoaderIconVisible(false);
        displayError(
          'We were unable to update the details. Please try again later'
        );
        logAmplitudeEvent(
          'Details_Updation_Failed',
          {
            selectedTab: 'PATIENT DETAILS',
            userMPID: userMPID,
            userName: values.name.trim(),
            dateOfBirth: values.dateOfBirth,
            gender: values.gender,
            emergencyContactName: values.emergencyContactName,
            emergencyContactMobile: values.emergencyContactMobile,
            email: values.email,
            edd: moment(values.edd).format('YYYY-MM-DD') || '',
            error: error
          },
          true
        );
      });
  };

  const patientDetailsSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Name must be minimum 3 characters !!!')
      .required('Required'),
    dateOfBirth: Yup.date().required('Please enter the date of birth'),
    mpid: Yup.string().required('Required'),
    registeredCenter: Yup.string().required('Required'),
    mobile: Yup.string().required('Required'),
    emergencyContactName: Yup.string().min(
      3,
      'Name must be minimum 3 characters !!!'
    ),
    gender: Yup.string()
      .required('Enter gender')
      .test('gender-validation', 'Invalid gender', function(value) {
        if (!value) return false;

        if (value.length < 4) {
          return this.createError({
            message: 'Gender must be minimum 4 characters'
          });
        }

        if (!['Male', 'Female'].includes(value)) {
          return this.createError({
            message: 'Gender must be either Male or Female'
          });
        }
        return true;
      }),

    emergencyContactMobile: Yup.string()
      .matches(/^[0-9]*$/, 'Please enter valid number!!!')
      .min(10, 'Please enter a valid 10 digit number !!!')
      .max(10, 'Please enter a valid 10 digit number !!!'),
    email: Yup.string()
      .nullable()
      .email('Invalid email')
      .required('Enter email'),
    edd: Yup.string()
      .nullable()
      .test('edd-date-range', 'Please enter a valid EDD', function(value) {
        if (!value) return true;

        const selectedDate = moment(value).startOf('day');
        const today = moment().startOf('day');
        const maxDate = today.clone().add(280, 'days');

        if (selectedDate.isBefore(today)) {
          return this.createError({ message: 'EDD cannot be in the past' });
        }
        if (selectedDate.isAfter(maxDate)) {
          return this.createError({
            message: 'EDD cannot be more than 280 days from today'
          });
        }
        return true;
      })
      .test(
        'female-min-age-18',
        'EDD can only be entered if the age is 18 years or above',
        function(value) {
          const { gender, dateOfBirth } = this.parent;
          if (!value) return true;

          if (gender !== 'Female') return true;

          if (!dateOfBirth) return true;

          const age = moment().diff(moment(dateOfBirth), 'years');

          return age >= 18;
        }
      ),
    departmentType: Yup.string(),
    visitReason: Yup.string(),
    source: Yup.string(),
    remarks: Yup.string().max(255, 'Only 255 characters are allowed'),
    vcReferral: Yup.string().max(100, 'Maximum 100 characters allowed'),
    pincode: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        const val = ~~originalValue;
        if (!val) {
          return null;
        }
        return Number(originalValue);
      })
      .test(
        'valid-pincode',
        'Invalid pincode',
        value =>
          value === null ||
          (Number.isInteger(value) && value >= 100000 && value <= 999999)
      ),
    hospitalId: Yup.number()
      .nullable()
      .when('edd', {
        is: edd => !!edd && !['DX_APP'].includes(dxLtEdd.source),
        then: schema =>
          schema.required('Hospital is required when EDD is selected'),
        otherwise: schema => schema.notRequired()
      })
  });

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Patient&#39;s Personal Details</ModalHeader>
      <ModalBody style={{ padding: '0px' }}>
        <TabContent activeTab={selectedTab}>
          <TabPane tabId={TABS.CUSTOMER_DETAILS}>
            {loaderIcon ? (
              <Loader />
            ) : selectedTab === TABS.CUSTOMER_DETAILS &&
              customerDetails !== [] ? (
              <Formik
                enableReinitialize={true}
                initialValues={{
                  name: `${
                    customerDetails.first_name
                  } ${customerDetails.last_name || ''}`,
                  dateOfBirth: customerDetails.date_of_birth,
                  mpid: customerDetails.mpid,
                  registeredCenter: customerDetails.registered_site_code,
                  mobile: customerDetails.mobile,
                  emergencyContactName: (
                    customerDetails.emergency_contact_name || ''
                  ).trim(),
                  gender: customerDetails.gender,
                  emergencyContactMobile: (
                    customerDetails.emergency_contact_mobile || ''
                  ).trim(),
                  email: customerDetails.email,
                  edd: dxLtEdd.edd || '',
                  departmentType: customerDetails.department_type || '',
                  visitReason: customerDetails.visit_reason || '',
                  source: customerDetails.source || '',
                  remarks: customerDetails.remarks || '',
                  vcReferral: customerDetails.vc_referral || '',
                  pincode:
                    customerDetails.address_pincode &&
                    customerDetails.address_pincode !== 'undefined'
                      ? customerDetails.address_pincode
                      : null,
                  hospitalId: ['DX_APP'].includes(dxLtEdd.source)
                    ? null
                    : (dxLtEdd.source === 'AMS' || dxLtEdd.source === 'LT') &&
                      customerDetails.hospital_id
                    ? customerDetails.hospital_id
                    : userAllocatedHospital.length === 1
                    ? userAllocatedHospital[0].value
                    : null
                }}
                validationSchema={patientDetailsSchema}
                onSubmit={handlePatientUpdate}
              >
                {({
                  handleChange,
                  handleSubmit,
                  handleBlur,
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  submitCount,
                  initialValues
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div style={{ padding: '14px' }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: 12
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '40%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>Name:</label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '75%',
                              flexDirection: 'column'
                            }}
                          >
                            <input
                              type="text"
                              onChange={handleChange('name')}
                              onBlur={handleBlur('name')}
                              value={values.name != null ? values.name : ''}
                            />
                            {errors.name && touched.name ? (
                              <ErrorMessage message={errors.name} />
                            ) : null}
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            width: '5%',
                            flexDirection: 'column'
                          }}
                        ></div>

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '60%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>
                              Date Of Birth:
                            </label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '50%',
                              flexDirection: 'column'
                            }}
                          >
                            <input
                              type="date"
                              name="dateOfBirth"
                              max={new Date().toISOString().split('T')[0]}
                              onChange={event => {
                                const dob = event.target.value;
                                setFieldValue('dateOfBirth', dob);
                              }}
                              value={values.dateOfBirth}
                            />
                            {errors.dateOfBirth && touched.dateOfBirth ? (
                              <ErrorMessage message={errors.dateOfBirth} />
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: 12
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '40%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>MPID:</label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '75%',
                              flexDirection: 'column'
                            }}
                          >
                            <input type="text" value={values.mpid} disabled />
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            width: '5%',
                            flexDirection: 'column'
                          }}
                        ></div>

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '60%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>
                              Registered Center:
                            </label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '50%',
                              flexDirection: 'column'
                            }}
                          >
                            <input
                              type="text"
                              value={values.registeredCenter}
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginTop: 12
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '40%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>Mobile:</label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '75%',
                              flexDirection: 'column'
                            }}
                          >
                            <input type="text" value={values.mobile} disabled />
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            width: '5%',
                            flexDirection: 'column'
                          }}
                        ></div>

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '60%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>
                              Emergency Contact Name:
                            </label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '50%',
                              flexDirection: 'column'
                            }}
                          >
                            <input
                              type="text"
                              onChange={handleChange('emergencyContactName')}
                              onBlur={handleBlur('emergencyContactName')}
                              value={values.emergencyContactName}
                            />
                            {errors.emergencyContactName &&
                            touched.emergencyContactName ? (
                              <ErrorMessage
                                message={errors.emergencyContactName}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginTop: 12
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '40%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>Gender:</label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '75%',
                              flexDirection: 'column'
                            }}
                          >
                            <input
                              type="text"
                              name="gender"
                              onChange={event => {
                                const gender = event.target.value;
                                setFieldValue('gender', gender);
                                if (
                                  gender === 'Male' ||
                                  (gender === 'Female' &&
                                    moment().diff(
                                      moment(values.dateOfBirth),
                                      'years'
                                    ) < 18)
                                ) {
                                  setFieldValue('edd', initialValues.edd);
                                  setFieldValue(
                                    'hospitalId',
                                    initialValues.hospitalId
                                  );
                                }
                              }}
                              onBlur={handleBlur('gender')}
                              value={values.gender}
                            />
                            {errors.gender && touched.gender ? (
                              <ErrorMessage message={errors.gender} />
                            ) : null}
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            width: '5%',
                            flexDirection: 'column'
                          }}
                        ></div>

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '60%'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              width: 'auto',
                              flexDirection: 'column'
                            }}
                          >
                            <label style={{ margin: '0px' }}>
                              Emergency Contact Number:
                            </label>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              width: '50%',
                              flexDirection: 'column'
                            }}
                          >
                            <input
                              type="text"
                              onChange={handleChange('emergencyContactMobile')}
                              onBlur={handleBlur('emergencyContactMobile')}
                              value={
                                values.emergencyContactMobile != null
                                  ? values.emergencyContactMobile
                                  : ''
                              }
                            />
                            {errors.emergencyContactMobile &&
                            touched.emergencyContactMobile ? (
                              <ErrorMessage
                                message={errors.emergencyContactMobile}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          marginTop: 12,
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '39%',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label htmlFor="edd" style={{ margin: '0px' }}>
                            EDD:
                          </label>
                          <div
                            style={{
                              display: 'flex',
                              width: '75%',
                              flexDirection: 'column',
                              position: 'relative'
                            }}
                          >
                            <Input
                              id="edd"
                              name="edd"
                              placeholder="EDD"
                              type="date"
                              value={values.edd}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={
                                dxLtEdd.source === 'DX_APP' ||
                                values.gender === 'Male' ||
                                (values.gender === 'Female' &&
                                  moment().diff(
                                    moment(values.dateOfBirth),
                                    'years'
                                  ) < 18)
                              }
                              style={{ height: '100%' }}
                            />
                            {errors.edd ? (
                              <span style={{ color: 'red' }}>{errors.edd}</span>
                            ) : null}

                            {dxLtEdd.edd ? (
                              <span
                                style={{
                                  position: 'absolute',
                                  right: 35,
                                  fontSize: '0.8rem',
                                  color: '#007BFF',
                                  top: '10px'
                                }}
                              >
                                Source:{' '}
                                {dxLtEdd.source === 'DX_APP'
                                  ? dxLtEdd.source
                                  : dxLtEdd.source}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            width: '5%',
                            flexDirection: 'column'
                          }}
                        ></div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '58%',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label htmlFor="edd" style={{ margin: '0px' }}>
                            Hospital:
                            {values.edd &&
                              (!dxLtEdd.source ||
                                dxLtEdd.source === 'AMS' ||
                                dxLtEdd.source === 'LT') && (
                                <span style={{ fontSize: '1em', color: 'red' }}>
                                  {' '}
                                  *
                                </span>
                              )}
                          </label>

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '50%'
                            }}
                          >
                            <DynamicSelect
                              name="hospitalId"
                              value={values.hospitalId}
                              options={userAllocatedHospital}
                              onChange={(field, value) =>
                                setFieldValue(field, value)
                              }
                              width="100%"
                              isDisabled={
                                !values.edd ||
                                !!errors.edd ||
                                ['DX_APP'].includes(dxLtEdd.source) ||
                                values.gender === 'Male' ||
                                (values.gender === 'Female' &&
                                  moment().diff(
                                    moment(values.dateOfBirth),
                                    'years'
                                  ) < 18)
                              }
                            />
                            {errors.hospitalId && submitCount > 0 ? (
                              <ErrorMessage message={errors.hospitalId} />
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'left',
                          alignItems: 'center',
                          marginTop: 13
                        }}
                      >
                        <label
                          style={{
                            margin: '0px',
                            width: '38%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginLeft: '3px'
                          }}
                        >
                          Email:&#160;
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '76%'
                            }}
                          >
                            <input
                              type="text"
                              onChange={handleChange('email')}
                              onBlur={handleBlur('email')}
                              value={values.email != null ? values.email : ''}
                            />
                            {errors.email && touched.email ? (
                              <ErrorMessage message={errors.email} />
                            ) : null}
                          </div>
                        </label>
                      </div>
                    </div>
                    <hr style={{ margin: '0px' }} />
                    <div
                      style={{
                        fontSize: '1.25rem',
                        fontWidth: 'bold',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      Patient&#39;s Welcome Call Details
                    </div>
                    <hr style={{ margin: '0px' }} />
                    <ModalBody>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(350px, 1fr))',
                          gap: '12px',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px'
                          }}
                        >
                          <label
                            htmlFor="departmentType"
                            style={{ margin: '0px' }}
                          >
                            Type:
                          </label>
                          <DynamicSelect
                            name="departmentType"
                            value={values.departmentType}
                            options={departmentType}
                            onChange={(field, value) => {
                              setFieldValue(field, value);
                              if (!value) {
                                setFieldValue('visitReason', '');
                              }
                            }}
                          />
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label
                            htmlFor="visitReason"
                            style={{ margin: '0px' }}
                          >
                            Visit Reason:
                          </label>

                          <DynamicSelect
                            name="visitReason"
                            value={values.visitReason}
                            options={
                              (values.departmentType &&
                                visitReason.find(f => {
                                  return values.departmentType === f.type;
                                }).options) ||
                              []
                            }
                            onChange={(field, value) =>
                              setFieldValue(field, value)
                            }
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label htmlFor="source" style={{ margin: '0px' }}>
                            Source:
                          </label>
                          <DynamicSelect
                            name="source"
                            value={values.source}
                            options={source}
                            onChange={(field, value) => {
                              setFieldValue(field, value);
                              if (
                                value !==
                                'Doctor Referral - Visiting Consultant'
                              ) {
                                setFieldValue('vcReferral', '');
                              }
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label htmlFor="pincode" style={{ margin: '0px' }}>
                            Pincode:
                          </label>

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '70%'
                            }}
                          >
                            <Input
                              type="number"
                              value={values.pincode}
                              onChange={e => {
                                const value = e.target.value;
                                setFieldValue(
                                  'pincode',
                                  value === '' ? null : Number(value)
                                );
                              }}
                            />
                            {touched.pincode && errors.pincode ? (
                              <span style={{ color: 'red' }}>
                                {errors.pincode}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label
                            htmlFor="remarks"
                            style={{ margin: '0px', marginTop: '8px' }}
                          >
                            Remarks:
                          </label>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '70%'
                            }}
                          >
                            <Input
                              style={{ width: '100%' }}
                              type="textarea"
                              value={values.remarks}
                              onChange={e => {
                                setFieldValue('remarks', e.target.value);
                              }}
                            />
                            {touched.remarks && errors.remarks ? (
                              <span style={{ color: 'red' }}>
                                {errors.remarks}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {values.source ===
                          'Doctor Referral - Visiting Consultant' && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              justifyContent: 'space-between'
                            }}
                          >
                            <label
                              htmlFor="vcReferral"
                              style={{ margin: '0px' }}
                            >
                              VC Referral:
                            </label>

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '70%'
                              }}
                            >
                              <Input
                                style={{ width: '100%' }}
                                type="text"
                                value={values.vcReferral}
                                onChange={e => {
                                  setFieldValue('vcReferral', e.target.value);
                                }}
                              />
                              {touched.vcReferral && errors.vcReferral ? (
                                <span style={{ color: 'red' }}>
                                  {errors.vcReferral}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    </ModalBody>

                    {detailsLoaderIconVisible ? (
                      <div
                        style={{
                          margin: 12,
                          marginRight: 15,
                          float: 'right'
                        }}
                      >
                        <Loader inline size={5} />
                      </div>
                    ) : (
                      <Button
                        color="primary"
                        style={{
                          margin: 12,
                          marginRight: 15,
                          float: 'right'
                        }}
                        type="submit"
                      >
                        UPDATE DETAILS
                      </Button>
                    )}
                  </form>
                )}
              </Formik>
            ) : null}
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

CustomerDetailsModal.propTypes = {
  userMPID: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired
};

export default CustomerDetailsModal;
